import flatMap from 'lodash/flatMap';
import noop from 'lodash/noop';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { useContentLibraryFilerOptionsQuery } from '../../graphql/types';
import { toggleHierarchicalFilter } from '../../utils/filter';

interface ClientContentAttribute {
  name: string;
  value: string;
  children?: ClientContentAttribute[];
  parentValue?: string;
}

const filterOptionsFromContentAttributes = (
  attributes: ClientContentAttribute[],
  key: string,
): ContentLibraryFilterOption<string>[] => {
  const flattenedAttributes = flatMap(attributes, (attribute) => [
    attribute,
    ...(attribute.children || []),
  ]);

  return flattenedAttributes.map((attribute) => {
    const parent = flattenedAttributes.find((a) =>
      a.children?.map((c) => c.value)?.includes(attribute.value),
    );
    return {
      label: attribute.name,
      queryValue: attribute.value,
      value: `${key}.${attribute.value}`,
      parentValue: parent?.value ? `${key}.${parent?.value}` : undefined,
      children: attribute.children?.map((c) => `${key}.${c.value}`) || [],
    };
  });
};

const contentFormatOptions: ContentLibraryFilterOption<string>[] = [
  { label: 'Article', queryValue: 'article', value: 'format.article' },
  { label: 'Video', queryValue: 'video', value: 'format.video' },
  { label: 'Audio', queryValue: 'audio', value: 'format.audio' },
];

const durationOptions: ContentLibraryFilterOption<[number, number]>[] = [
  { label: '0-5 min', queryValue: [0, 5], value: 'duration.0-5' },
  { label: '5-15 min', queryValue: [5, 15], value: 'duration.5-15' },
  { label: '15-30 min', queryValue: [15, 30], value: 'duration.15-30' },
  { label: '30+ min', queryValue: [30, 99999], value: 'duration.30+' },
];

const isExperientialOptions: ContentLibraryFilterOption<boolean>[] = [
  { label: 'Is experiential', queryValue: true, value: 'isExperiential.true' },
  {
    label: 'Is not experiential',
    queryValue: false,
    value: 'isExperiential.false',
  },
];

export interface ContentLibraryFilterOption<T> {
  label: string;
  queryValue: T;
  value: string;
  parentValue?: string;
  children?: string[];
}

interface ContentLibraryFilterContextType {
  setAppliedFilters: Dispatch<SetStateAction<string[]>>;
  toggleFilter: (filterKey: string) => void;
  appliedFilters: {
    formats: ContentLibraryFilterOption<string>[];
    genres: ContentLibraryFilterOption<string>[];
    gameplanPrimaryComponents: ContentLibraryFilterOption<string>[];
    gameplanPrimarySubcomponents: ContentLibraryFilterOption<string>[];
    gameplanSecondaryComponents: ContentLibraryFilterOption<string>[];
    gameplanSecondarySubcomponents: ContentLibraryFilterOption<string>[];
    associatedProject: ContentLibraryFilterOption<string>[];
    intensities: ContentLibraryFilterOption<string>[];
    levels: ContentLibraryFilterOption<string>[];
    splits: ContentLibraryFilterOption<string>[];
    duration?: ContentLibraryFilterOption<[number, number]>;
    performanceQuality?: ContentLibraryFilterOption<string>;
    all: ContentLibraryFilterOption<string | number[] | boolean>[];
    isExperiential?: ContentLibraryFilterOption<boolean>;
  };
  search: {
    title?: string;
  };
  setSearch: Dispatch<SetStateAction<Record<string, string | undefined>>>;
  options: {
    formats: ContentLibraryFilterOption<string>[];
    genres: ContentLibraryFilterOption<string>[];
    gameplanPrimaryComponents: ContentLibraryFilterOption<string>[];
    gameplanPrimarySubcomponents: ContentLibraryFilterOption<string>[];
    gameplanSecondaryComponents: ContentLibraryFilterOption<string>[];
    gameplanSecondarySubcomponents: ContentLibraryFilterOption<string>[];
    associatedProject: ContentLibraryFilterOption<string>[];
    intensities: ContentLibraryFilterOption<string>[];
    levels: ContentLibraryFilterOption<string>[];
    splits: ContentLibraryFilterOption<string>[];
    durations: ContentLibraryFilterOption<[number, number]>[];
    performanceQualities: ContentLibraryFilterOption<string>[];
    isExperiential: ContentLibraryFilterOption<boolean>[];
    all: ContentLibraryFilterOption<string | boolean | [number, number]>[];
  };
}

const defaultValue: ContentLibraryFilterContextType = {
  options: {
    formats: [],
    genres: [],
    gameplanPrimaryComponents: [],
    gameplanPrimarySubcomponents: [],
    gameplanSecondaryComponents: [],
    gameplanSecondarySubcomponents: [],
    associatedProject: [],
    intensities: [],
    levels: [],
    splits: [],
    durations: [],
    performanceQualities: [],
    all: [],
    isExperiential: [],
  },
  appliedFilters: {
    formats: [],
    genres: [],
    gameplanPrimaryComponents: [],
    gameplanPrimarySubcomponents: [],
    gameplanSecondaryComponents: [],
    gameplanSecondarySubcomponents: [],
    associatedProject: [],
    intensities: [],
    levels: [],
    splits: [],
    duration: undefined,
    performanceQuality: undefined,
    all: [],
    isExperiential: undefined,
  },
  search: {
    title: undefined,
  },
  setSearch: noop,
  setAppliedFilters: noop,
  toggleFilter: noop,
};

const ContentLibraryFilterContext =
  React.createContext<ContentLibraryFilterContextType>(defaultValue);

export const ContentLibraryFilterProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [search, setSearch] = useState<Record<string, string | undefined>>({
    search: undefined,
  });
  const { data: filterOptions } = useContentLibraryFilerOptionsQuery();

  const [
    contentGenreOptions,
    gameplanPrimaryComponentOptions,
    gameplanPrimarySubcomponentOptions,
    gameplanSecondaryComponentOptions,
    gameplanSecondarySubcomponentOptions,
    contentProjectOptions,
    contentIntensityOptions,
    contentLevelOptions,
    contentSplitOptions,
    performanceQualityOptions,
  ] = Object.entries({
    genre: filterOptions?.contentGenres,
    gameplanPrimaryComponent: filterOptions?.gameplanComponents,
    gameplanPrimarySubcomponents: filterOptions?.gameplanSubcomponents,
    gameplanSecondaryComponent: filterOptions?.gameplanComponents,
    gameplanSecondarySubcomponents: filterOptions?.gameplanSubcomponents,
    associatedProject: filterOptions?.contentProjects,
    intensity: filterOptions?.contentIntensities,
    level: filterOptions?.contentLevels,
    contentSplitOptions: filterOptions?.contentSplits,
    performanceQualityOptions: filterOptions?.performanceQualities,
  }).map(([label, options]) =>
    filterOptionsFromContentAttributes(options || [], label),
  );

  const appliedDuration = durationOptions.find((d) =>
    appliedFilters.includes(d.value),
  );
  const appliedPerformanceQuality = performanceQualityOptions.find((d) =>
    appliedFilters.includes(d.value),
  );
  const appliedIsExperiential = isExperientialOptions.find((d) =>
    appliedFilters.includes(d.value),
  );
  const [
    appliedFormats,
    appliedGenres,
    appliedPrimaryGameplanComponents,
    appliedPrimaryGameplanSubcomponents,
    appliedSecondaryGameplanComponents,
    appliedSecondaryGameplanSubcomponents,
    appliedAssociateProjects,
    appliedIntensities,
    appliedLevels,
    appliedSplits,
  ] = [
    contentFormatOptions,
    contentGenreOptions,
    gameplanPrimaryComponentOptions,
    gameplanPrimarySubcomponentOptions,
    gameplanSecondaryComponentOptions,
    gameplanSecondarySubcomponentOptions,
    contentProjectOptions,
    contentIntensityOptions,
    contentLevelOptions,
    contentSplitOptions,
  ].map((options) =>
    options.filter((option) => appliedFilters.includes(option.value)),
  );

  const allOptions = React.useMemo(
    () => [
      ...contentFormatOptions,
      ...contentGenreOptions,
      ...gameplanPrimaryComponentOptions,
      ...gameplanPrimarySubcomponentOptions,
      ...gameplanSecondaryComponentOptions,
      ...gameplanSecondarySubcomponentOptions,
      ...contentProjectOptions,
      ...contentIntensityOptions,
      ...contentLevelOptions,
      ...durationOptions,
      ...performanceQualityOptions,
      ...isExperientialOptions,
      ...contentSplitOptions,
    ],
    [
      contentGenreOptions,
      gameplanPrimaryComponentOptions,
      gameplanPrimarySubcomponentOptions,
      gameplanSecondaryComponentOptions,
      gameplanSecondarySubcomponentOptions,
      contentProjectOptions,
      contentIntensityOptions,
      contentLevelOptions,
      performanceQualityOptions,
      contentSplitOptions,
    ],
  );

  const allAppliedFilters = React.useMemo(
    () => [
      ...appliedFormats,
      ...appliedGenres,
      ...appliedPrimaryGameplanComponents,
      ...appliedPrimaryGameplanSubcomponents,
      ...appliedSecondaryGameplanComponents,
      ...appliedSecondaryGameplanSubcomponents,
      ...appliedAssociateProjects,
      ...appliedSplits,
      ...appliedIntensities,
      ...appliedLevels,
      ...(appliedPerformanceQuality ? [appliedPerformanceQuality] : []),
      ...(appliedDuration ? [appliedDuration] : []),
      ...(appliedIsExperiential ? [appliedIsExperiential] : []),
    ],
    [
      appliedFormats,
      appliedGenres,
      appliedPrimaryGameplanComponents,
      appliedPrimaryGameplanSubcomponents,
      appliedSecondaryGameplanComponents,
      appliedSecondaryGameplanSubcomponents,
      appliedAssociateProjects,
      appliedSplits,
      appliedIntensities,
      appliedLevels,
      appliedPerformanceQuality,
      appliedDuration,
      appliedIsExperiential,
    ],
  );

  const toggleFilter = React.useCallback(
    (filterValue: string) => {
      const newFilters = toggleHierarchicalFilter(
        allAppliedFilters.map((f) => f.value),
        filterValue,
        allOptions,
      );
      return setAppliedFilters(newFilters);
    },
    [allAppliedFilters, allOptions],
  );

  const value = React.useMemo(
    () => ({
      toggleFilter,
      setAppliedFilters,
      appliedFilters: {
        formats: appliedFormats,
        genres: appliedGenres,
        gameplanPrimaryComponents: appliedPrimaryGameplanComponents,
        gameplanPrimarySubcomponents: appliedPrimaryGameplanSubcomponents,
        gameplanSecondaryComponents: appliedSecondaryGameplanComponents,
        gameplanSecondarySubcomponents: appliedSecondaryGameplanSubcomponents,
        associatedProject: appliedAssociateProjects,
        intensities: appliedIntensities,
        levels: appliedLevels,
        splits: appliedSplits,
        duration: appliedDuration,
        performanceQuality: appliedPerformanceQuality,
        isExperiential: appliedIsExperiential,
        all: allAppliedFilters,
      },
      search: {
        title: search.title,
      },
      setSearch,
      options: {
        formats: contentFormatOptions,
        genres: contentGenreOptions,
        gameplanPrimaryComponents: gameplanPrimaryComponentOptions,
        gameplanPrimarySubcomponents: gameplanPrimarySubcomponentOptions,
        gameplanSecondaryComponents: gameplanSecondaryComponentOptions,
        gameplanSecondarySubcomponents: gameplanSecondarySubcomponentOptions,
        associatedProject: contentProjectOptions,
        intensities: contentIntensityOptions,
        levels: contentLevelOptions,
        splits: contentSplitOptions,
        durations: durationOptions,
        performanceQualities: performanceQualityOptions,
        isExperiential: isExperientialOptions,
        all: allOptions,
      },
    }),
    [
      appliedAssociateProjects,
      appliedIntensities,
      appliedLevels,
      appliedPrimaryGameplanComponents,
      appliedPrimaryGameplanSubcomponents,
      appliedSecondaryGameplanComponents,
      appliedSecondaryGameplanSubcomponents,
      appliedDuration,
      appliedPerformanceQuality,
      appliedFormats,
      appliedGenres,
      appliedSplits,
      search.title,
      contentGenreOptions,
      gameplanPrimaryComponentOptions,
      gameplanPrimarySubcomponentOptions,
      gameplanSecondaryComponentOptions,
      gameplanSecondarySubcomponentOptions,
      contentProjectOptions,
      contentIntensityOptions,
      contentLevelOptions,
      contentSplitOptions,
      performanceQualityOptions,
      appliedIsExperiential,
      toggleFilter,
      allAppliedFilters,
      allOptions,
    ],
  );

  return (
    <ContentLibraryFilterContext.Provider value={value}>
      {children}
    </ContentLibraryFilterContext.Provider>
  );
};

export const TestContentLibraryFilterContextProvider: React.FC<
  React.PropsWithChildren & {
    value?: Partial<ContentLibraryFilterContextType>;
  }
> = ({ children, value }) => {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error(
      'getContentLibraryFilterContextForTesting should only be used in tests',
    );
  }
  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <ContentLibraryFilterContext.Provider value={{ ...defaultValue, ...value }}>
      {children}
    </ContentLibraryFilterContext.Provider>
  );
};

export function useContentLibraryFilterContext(): ContentLibraryFilterContextType {
  return React.use(ContentLibraryFilterContext);
}
